import { createAsyncThunk } from '@reduxjs/toolkit';
import { loadUsersAndPersonalResourcesByUserIds } from 'store/projectUsers/thunks/loadUsersAndPersonalResourcesByUserIds';
import { selectUserActiveEmployerInfo } from 'store/user/selectors.js/user.selectors';
import { getProjectActivityFeed } from '../services/getProjectActivityFeed.service';

/** Rows of the dashboard's feed, and of each page the dialog loads more of. */
export const PROJECT_ACTIVITY_FEED_PAGE_SIZE = 20;

/**
 * The most API pages one load requests while none of them has a row to show.
 * Each costs the API at most 1000 examined rows, so a project whose newest
 * thousands of rows are all left out still stops here, keeping the cursor.
 */
export const PROJECT_ACTIVITY_FEED_MAX_PAGES_PER_LOAD = 5;

/**
 * Rows asked for per request when a load covers everything since a moment
 * (the dashboard's day): the most the API gives in one page.
 */
export const PROJECT_ACTIVITY_FEED_SINCE_PAGE_SIZE = 50;

/**
 * The most API pages a `since` load requests. Each request costs the API at
 * most 10 queries of 100 examined rows, so a load stops after at most
 * 10 x 50 = 500 rows shown (or 10 000 examined, were they nearly all left
 * out). A busier stretch is loaded newest first up to there, and the load
 * says it is `incomplete`.
 */
export const PROJECT_ACTIVITY_FEED_MAX_PAGES_SINCE = 10;

// Someone opening the project is the most common row of the log and says
// little, so the feed does not show it. The API leaves it out as well; this
// keeps it out when the API is one that does not yet.
export const isShownInProjectFeed = (item) => item.action !== 'VIEW_PROJECT';

// Whether a cursor (`{createdAtISO}#{uuid}`, the sort key of the last row
// examined) still lies at or after `since` (an ISO timestamp, as createdAt is
// written): older rows of that stretch may follow. Both are UTC ISO strings
// of the same shape, so they compare as text.
const cursorReaches = (cursor, since) => Boolean(cursor) && cursor >= since;

/**
 * Loads a page of the project's activity feed: the newest rows, or with
 * `before` (the previous page's `nextBefore`) the next older page. Users the
 * store does not know yet are loaded alongside, for their profile pictures.
 * Resolves `{ items, nextBefore, images }` with only the rows the feed shows;
 * `images` is what the viewer may see of the images the rows are about (file
 * permission id -> the API's verdict, see resolveActivityLogImages), merged
 * from every API page the load asked for — empty from an API that does not
 * resolve them yet.
 *
 * With `since` (an ISO timestamp, the dashboard's local midnight) it loads
 * everything from the newest row back to `since` instead: pages of
 * PROJECT_ACTIVITY_FEED_SINCE_PAGE_SIZE until the cursor passes `since`, the
 * feed ends, or PROJECT_ACTIVITY_FEED_MAX_PAGES_SINCE pages have been asked
 * for. The last page can reach past `since` (the caller leaves those rows
 * out of its day, as the project dialog does). It resolves
 * `{ items, nextBefore, images, incomplete }`, `incomplete` when the page limit
 * stopped it before `since`. The API need not know `since`: the cursor alone
 * tells where it is.
 *
 * An API page can hold nothing to show while older rows follow (the rows of
 * opening the project filled it), so this follows `nextBefore` until a page
 * has a row to show, the feed ends, or PROJECT_ACTIVITY_FEED_MAX_PAGES_PER_LOAD
 * pages have been asked for. Without that the dashboard, which loads only the
 * first page, stayed blank until the dialog's "Lataa lisää" loaded older ones.
 */
export const fetchProjectActivityFeed = createAsyncThunk(
  'activityLog/fetchProjectFeed',
  async ({ projectId, before, since }, { dispatch, getState }) => {
    const state = getState();
    const activeEmployerInfo = selectUserActiveEmployerInfo(state);

    let items = [];
    let images = {};
    let cursor = before;
    let incomplete = false;
    if (since) {
      for (
        let pages = 0;
        pages < PROJECT_ACTIVITY_FEED_MAX_PAGES_SINCE;
        pages += 1
      ) {
        const data = await getProjectActivityFeed({
          projectId,
          limit: PROJECT_ACTIVITY_FEED_SINCE_PAGE_SIZE,
          before: cursor,
          activeEmployerInfo,
        });
        items = items.concat((data?.items || []).filter(isShownInProjectFeed));
        images = { ...images, ...(data?.images || {}) };
        cursor = data?.nextBefore || null;
        if (!cursorReaches(cursor, since)) break;
      }
      incomplete = cursorReaches(cursor, since);
    } else {
      for (
        let pages = 0;
        pages < PROJECT_ACTIVITY_FEED_MAX_PAGES_PER_LOAD;
        pages += 1
      ) {
        const data = await getProjectActivityFeed({
          projectId,
          limit: PROJECT_ACTIVITY_FEED_PAGE_SIZE,
          before: cursor,
          activeEmployerInfo,
        });
        items = items.concat((data?.items || []).filter(isShownInProjectFeed));
        images = { ...images, ...(data?.images || {}) };
        cursor = data?.nextBefore || null;
        if (items.length > 0 || !cursor) break;
      }
    }

    const knownUsers = state.projectUsers?.users?.entities || {};
    const missingUserIds = [
      ...new Set(items.map((item) => item.userId).filter(Boolean)),
    ].filter((userId) => !knownUsers[userId]);
    if (missingUserIds.length > 0) {
      dispatch(loadUsersAndPersonalResourcesByUserIds(missingUserIds));
    }

    return since
      ? { items, nextBefore: cursor, images, incomplete }
      : { items, nextBefore: cursor, images };
  }
);
